import React, { Fragment, useEffect, useMemo, useState } from 'react';
import KPICard, { KPIGrid } from '../KPICard';
import BarChart from '../charts/BarChart';
import LineChart from '../charts/LineChart';
import DataTable from '../DataTable';
import { formatCurrency, formatInt } from '../../utils/format';
import { evaluateDeal, evaluateAllScenarios } from '../../engine/dealEngine';
import { buildBoswellDcaChallenger, volAdjForScenario } from '../../engine/dca';
import { categoriesFromAssumptions } from '../../engine/buyerOverhead';
import { useAssistantPageExtras } from '../assistant/AssistantContext';
import { useAuth } from '../../auth/AuthContext';
import { withAroEstimate } from '../../aro/api';
import { fetchDeskJson } from '../../lib/deskDataFetch';
import { track } from '../../lib/track';
import { navigateToWell } from '../../utils/wellIdentity';
import {
  coerceFinite,
  deskKnobsDiffer,
  loadDeskKnobs,
  parseNumericDraft,
  syncDeskToHash,
  rememberLiveDeskKnobs,
} from '../../engine/deskPersistence';
import { useDeal } from '../../deals/DealContext';
import { dealDataUrl } from '../../deals/registry';
import { isMultiProductPackage } from '../../engine/productStreams';

export default function Sensitivity({ data }) {
  const { boswell, midstream, midstreamAssignability, quote, quoteLabel, engineNote, liveEngine } =
    data;
  const auth = useAuth();
  const { setPageExtras, clearPageExtras } = useAssistantPageExtras();
  const { deal } = useDeal();
  const [pkg, setPkg] = useState(null);
  const [hh, setHh] = useState(3.42);
  const [boswellVolAdj, setBoswellVolAdj] = useState(0);
  const [netbackAdj, setNetbackAdj] = useState(0);
  const [discountRate, setDiscountRate] = useState(0.1);
  const [aroMM, setAroMM] = useState(0);
  const [buyerOhCategories, setBuyerOhCategories] = useState(() => categoriesFromAssumptions({}));
  const [loadError, setLoadError] = useState(null);
  const [dcaCase, setDcaCase] = useState('aries');
  const [useHyperbolic, setUseHyperbolic] = useState(false);
  const [restoreNote, setRestoreNote] = useState(null);
  const [persistReady, setPersistReady] = useState(false);
  const [hhDraft, setHhDraft] = useState(null);
  const dealId = deal?.dealId;

  // Remount via App key={dealId} is primary; gate persist until package reload finishes.
  useEffect(() => {
    setPersistReady(false);
    setRestoreNote(null);
    setHhDraft(null);
  }, [dealId]);

  useEffect(() => {
    if (!liveEngine) return undefined;
    let cancelled = false;
    fetchDeskJson(dealDataUrl(deal, 'deal-package.json', import.meta.env.BASE_URL), {
      refreshSession: () => auth.refresh(),
      errorMessage: (status) => `deal-package.json ${status}`,
    })
      .then((p) => (p && auth.isInternal ? withAroEstimate(p, dealId) : p))
      .then((p) => {
        if (cancelled || !p) return;
        setPkg(p);
        const isOilPkg = (p.meta?.primaryProduct || p.meta?.product) === 'oil';
        const multiPkg =
          isOilPkg &&
          (String(p.meta?.commodity || '').includes('gas') ||
            (Array.isArray(p.meta?.products) &&
              p.meta.products.some((x) => /gas|casinghead|residue/i.test(String(x)))));
        const pkgDefaults = {
          hh: isOilPkg
            ? (p.assumptions?.wti ??
              p.meta?.wtiBase ??
              (multiPkg ? 75 : p.assumptions?.hh) ??
              p.meta?.hhBase ??
              75)
            : (p.assumptions?.hh ?? p.meta?.hhBase ?? 3.42),
          boswellVolAdj: p.assumptions?.boswellVolAdj ?? 0,
          netbackAdj: p.assumptions?.netbackAdj ?? 0,
          discountRate: p.assumptions?.discountRate ?? 0.1,
          aroMM: p.assumptions?.aroMM ?? 0,
          buyerOverheadCategories: categoriesFromAssumptions(p.assumptions || {}),
        };
        const restored = loadDeskKnobs(pkgDefaults, { dealId });
        setHh(coerceFinite(restored.hh, pkgDefaults.hh));
        setBoswellVolAdj(coerceFinite(restored.boswellVolAdj, 0));
        setNetbackAdj(coerceFinite(restored.netbackAdj, 0));
        setDiscountRate(coerceFinite(restored.discountRate, pkgDefaults.discountRate));
        // Configured ARO (D2D estimate / override) is authoritative over a stale stored knob.
        setAroMM(
          p.assumptions?.aroSource
            ? pkgDefaults.aroMM
            : Math.max(0, coerceFinite(restored.aroMM, 0)),
        );
        setBuyerOhCategories(
          restored.buyerOverheadCategories?.length
            ? categoriesFromAssumptions({
                buyerOverheadCategories: restored.buyerOverheadCategories,
              })
            : pkgDefaults.buyerOverheadCategories,
        );
        if (
          deskKnobsDiffer(restored, pkgDefaults, {
            fields: ['hh', 'boswellVolAdj', 'netbackAdj', 'discountRate', 'aroMM'],
          })
        ) {
          setRestoreNote('Restored desk knobs from URL / prior session');
        }
        setPersistReady(true);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [liveEngine, deal, dealId, auth.refresh]);

  useEffect(() => {
    if (!persistReady || !liveEngine) return undefined;
    const knobs = {
      hh,
      boswellVolAdj,
      netbackAdj,
      discountRate,
      aroMM,
      buyerOverheadCategories: buyerOhCategories,
    };
    rememberLiveDeskKnobs(knobs, { dealId });
    const t = window.setTimeout(() => {
      syncDeskToHash(knobs, { path: 'sensitivity' });
    }, 200);
    return () => {
      window.clearTimeout(t);
      rememberLiveDeskKnobs(knobs, { dealId });
    };
  }, [persistReady, hh, boswellVolAdj, netbackAdj, discountRate, aroMM, buyerOhCategories, liveEngine, dealId]);

  function handleBuyerOhChange(id, patch) {
    setBuyerOhCategories((cats) =>
      cats.map((c) => {
        if (c.id !== id) return c;
        const merged = { ...c, ...patch };
        if ('monthly' in patch) {
          const n = Number(patch.monthly);
          merged.monthly = Number.isFinite(n) ? Math.max(0, n) : c.monthly;
        }
        return merged;
      }),
    );
  }

  function onHhDraftChange(raw) {
    setHhDraft(raw);
    if (raw === '' || raw === '.' || raw === '-' || raw === '-.') return;
    const parsed = parseNumericDraft(raw, { empty: null, fallback: null, min: 0 });
    if (parsed == null) return;
    setHh(parsed);
  }

  function onHhDraftBlur() {
    if (hhDraft == null) return;
    if (hhDraft === '' || hhDraft === '.' || hhDraft === '-' || hhDraft === '-.') {
      setHhDraft(null);
      return;
    }
    const parsed = parseNumericDraft(hhDraft, { empty: null, fallback: hh, min: 0 });
    setHh(coerceFinite(parsed, hh));
    setHhDraft(null);
  }

  const isOilDeal = (pkg?.meta?.primaryProduct || pkg?.meta?.product) === 'oil';
  const multiProduct = Boolean(pkg && isMultiProductPackage(pkg));
  const packageGasHh = multiProduct
    ? (pkg?.assumptions?.hh ?? pkg?.meta?.hhBase ?? 3.42)
    : null;
  const priceMax = isOilDeal ? 120 : 4.5;
  const priceStep = isOilDeal ? 0.5 : 0.05;
  const priceLabel = isOilDeal ? 'WTI strip ($/bbl)' : 'HH strip ($/MMBtu)';
  const priceAria = isOilDeal ? 'WTI strip price' : 'HH strip price';
  const volLabel = isOilDeal ? 'Concentration volume' : 'Boswell volume';
  const rateUnit = isOilDeal ? 'bbl/d' : 'Mcf/d';
  const wellLabel = isOilDeal ? 'Largest well' : 'Boswell';

  const deskPriceOverrides = useMemo(() => {
    if (isOilDeal && multiProduct) return { wti: hh, hh: packageGasHh };
    if (isOilDeal) return { hh, wti: hh };
    return { hh };
  }, [isOilDeal, multiProduct, hh, packageGasHh]);

  const deskKnobOverrides = useMemo(
    () => ({
      ...deskPriceOverrides,
      boswellVolAdj,
      netbackAdj,
      discountRate,
      aroMM,
      buyerOverheadCategories: buyerOhCategories,
    }),
    [deskPriceOverrides, boswellVolAdj, netbackAdj, discountRate, aroMM, buyerOhCategories],
  );

  const live = useMemo(() => {
    if (!pkg) return null;
    return evaluateDeal(pkg, deskKnobOverrides);
  }, [pkg, deskKnobOverrides]);

  const scenarioRows = useMemo(() => {
    if (!pkg) {
      // sensitivity.json uses pv10; live engine rows use pv — normalize for the table/chart.
      return (data.bidScenarios || []).map((s) => {
        const pv = s.pv ?? s.pv10;
        return {
          id: s.id,
          scenario: s.scenario || s.label,
          pv,
          ntm: s.ntm,
          x4: s.x4,
          x5: s.x5,
          x6: s.x6,
          pv08: s.pv08 ?? (pv != null ? +(pv * 0.8).toFixed(2) : null),
        };
      });
    }
    // Same desk knobs as live KPI — scenario deltas still win on conflict.
    return evaluateAllScenarios(pkg, deskKnobOverrides).map((s) => ({
      id: s.id,
      scenario: s.label,
      pv: +s.result.pvMM.toFixed(2),
      ntm: +s.result.ntmCfMM.toFixed(2),
      x4: +s.result.multiples.x4.toFixed(2),
      x5: +s.result.multiples.x5.toFixed(2),
      x6: +s.result.multiples.x6.toFixed(2),
      pv08: +(s.result.pvMM * 0.8).toFixed(2),
    }));
  }, [pkg, data.bidScenarios, deskKnobOverrides]);

  const fmtMM = (v) => (v == null || Number.isNaN(Number(v)) ? '—' : `$${Number(v).toFixed(2)}M`);

  const bearX5 = scenarioRows.find((s) => s.id === 'bear')?.x5;
  const refPdp = pkg?.validation?.refPdpPv10MM;
  const modelBase = pkg?.validation?.modelBasePv10MM;

  const dca = useMemo(() => {
    const actuals = pkg?.actuals?.boswell;
    if (!actuals && !boswell) return null;
    const ariesAsset = pkg?.assets?.find((a) => a.isBoswell);
    return buildBoswellDcaChallenger({
      actuals: actuals || {
        annual: boswell.annual,
        recentMonths: boswell.recentMonths,
        stats: {
          troughMcfpd: boswell.troughMcfpd,
          peakRecentMcfpd: boswell.postAcidWeekly,
          acid: { last30AvgMcfpd: boswell.postAcidWeekly, stepPct: boswell.acidStepPct },
        },
      },
      ariesQi: ariesAsset?.qiMcfd || ariesAsset?.qiBopd || boswell.ariesQi || null,
      ariesDeAnnual: ariesAsset?.deAnnual || 0.06,
      startDate: (ariesAsset?.start || '2026-06-01').slice(0, 7),
      useHyperbolic,
      rateUnit,
    });
  }, [pkg, boswell, useHyperbolic, rateUnit]);

  const diag = dca?.diagnostics || null;

  const dcaPvRows = useMemo(() => {
    if (!pkg || !dca) return [];
    return dca.scenarios.map((s) => {
      const adj = s.volAdjVsAries;
      const r = evaluateDeal(pkg, {
        ...deskKnobOverrides,
        boswellVolAdj: adj,
      });
      const baseR = evaluateDeal(pkg, {
        ...deskKnobOverrides,
        boswellVolAdj: 0,
      });
      return {
        id: s.id,
        scenario: s.label,
        class: s.class,
        qi: Math.round(s.qi),
        volAdj: adj,
        cum12: s.cum12Mmcf,
        cum60: s.cum60Mmcf,
        pv: +r.pvMM.toFixed(2),
        dPv: +(r.pvMM - baseR.pvMM).toFixed(2),
        note: s.note,
      };
    });
  }, [pkg, dca, deskKnobOverrides]);

  function applyDcaToKnob(scenarioId) {
    if (!dca) return;
    const adj = volAdjForScenario(dca, scenarioId);
    setDcaCase(scenarioId);
    setBoswellVolAdj(adj);
    track('dca_challenger_applied', { scenario: scenarioId, boswell_vol_adj: adj });
  }

  useEffect(() => {
    if (!live) {
      clearPageExtras();
      return undefined;
    }
    setPageExtras({
      tab: 'sensitivity',
      hh,
      boswellVolAdj,
      netbackAdj,
      discountRate,
      aroMM,
      buyerOhMonthlyIncluded: live.buyerOverhead?.monthlyIncluded ?? null,
      pvLabel: live.pvLabel,
      pvMM: +live.pvMM.toFixed(3),
      ntmCfMM: +live.ntmCfMM.toFixed(3),
      boswellShare: +live.boswellShare.toFixed(4),
      midstreamStatus: midstreamAssignability?.overallStatus ?? null,
      stackAt6k: midstream?.stackAt6k ?? null,
      bearX5MM: bearX5 ?? null,
      dcaCase,
      dcaVerdict: dca?.verdict ?? null,
      dcaTrough: dca?.troughMcfpd ?? null,
      dcaPostAcid: dca?.postAcidMcfpd ?? null,
    });
    return () => clearPageExtras();
  }, [
    live,
    hh,
    boswellVolAdj,
    netbackAdj,
    discountRate,
    aroMM,
    midstreamAssignability,
    midstream,
    bearX5,
    dcaCase,
    dca,
    setPageExtras,
    clearPageExtras,
  ]);

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <div className="page-header-left">
          <h2 className="page-title">
            {isOilDeal ? 'Price · Volume · Sensitivity' : 'Boswell · Midstream · Sensitivity'}
          </h2>
          <p className="page-subtitle">
            Live DealPackage knobs · bid/IRR on Decision desk · multiples = back-check
          </p>
          <div className="page-quote">
            <span className="page-quote-label">{quoteLabel}</span>
            <span>&ldquo;{quote}&rdquo;</span>
          </div>
          {engineNote && <p className="engine-note">{engineNote}</p>}
          {loadError && <p className="engine-note error">Engine load failed: {loadError}</p>}
          {live?.hasMissingStreams && (
            <p className="engine-note error" role="status">
              Warning: {live.missingStreamCount} asset
              {live.missingStreamCount === 1 ? '' : 's'} lack stream months and contribute $0 to
              package PV (
              {live.missingStreams
                .slice(0, 3)
                .map((a) => a.lease || a.propnum)
                .join(', ')}
              {live.missingStreamCount > 3 ? ', …' : ''}).
            </p>
          )}
          <button
            type="button"
            className="btn-secondary"
            style={{ marginTop: 10 }}
            onClick={() => {
              const a = pkg?.assets?.find((x) => (isOilDeal ? x.isConcentration || x.isBoswell : x.isBoswell));
              navigateToWell(a || { lease: isOilDeal ? 'largest well' : 'BOSWELL 1' });
            }}
          >
            {isOilDeal ? 'Open largest well →' : 'Open Boswell well detail →'}
          </button>
        </div>
      </div>

      {pkg && (
        <section className="dashboard-section">
          <div className="chart-card assumption-panel">
            <div className="chart-title">Assumption knobs (live)</div>
            <div className="chart-subtitle">
              Calibration: model base PV10 ${modelBase ?? '—'}MM vs seller PDP ${refPdp ?? '—'}MM
              {modelBase != null && refPdp != null
                ? ` (Δ $${(modelBase - refPdp).toFixed(2)}MM)`
                : ''}
              . Streams are decline-reconstructed + cfScale-fitted to seller oneline. Knobs sync with
              Decision via URL hash / local session.
            </div>
            {restoreNote && (
              <p className="engine-note desk-restore-note" role="status">
                {restoreNote}{' '}
                <button
                  type="button"
                  className="linkish"
                  onClick={() => {
                    if (!pkg) return;
                    setHh(
                      isOilDeal
                        ? (pkg.assumptions?.wti ??
                          pkg.meta?.wtiBase ??
                          pkg.assumptions?.hh ??
                          pkg.meta?.hhBase ??
                          75)
                        : (pkg.assumptions?.hh ?? pkg.meta?.hhBase ?? 3.42),
                    );
                    setBoswellVolAdj(pkg.assumptions?.boswellVolAdj ?? 0);
                    setNetbackAdj(pkg.assumptions?.netbackAdj ?? 0);
                    setDiscountRate(pkg.assumptions?.discountRate ?? 0.1);
                    setAroMM(pkg.assumptions?.aroMM ?? 0);
                    setBuyerOhCategories(categoriesFromAssumptions(pkg.assumptions || {}));
                    setRestoreNote(null);
                  }}
                >
                  Reset to package defaults
                </button>
              </p>
            )}
            <div className="assumption-grid">
              <label className="assumption-control">
                <span>{priceLabel}</span>
                <input
                  type="range"
                  min={0}
                  max={priceMax}
                  step={priceStep}
                  value={Math.min(priceMax, Math.max(0, hh))}
                  onChange={(e) => {
                    setHhDraft(null);
                    setHh(Number(e.target.value));
                  }}
                />
                <input
                  className="assumption-number"
                  type="number"
                  min={0}
                  step={priceStep}
                  value={hhDraft != null ? hhDraft : hh}
                  aria-label={priceAria}
                  onChange={(e) => onHhDraftChange(e.target.value)}
                  onBlur={onHhDraftBlur}
                />
              </label>
              <label className="assumption-control">
                <span>
                  {volLabel} ({(boswellVolAdj * 100).toFixed(0)}%)
                </span>
                <input
                  type="range"
                  min={-0.5}
                  max={0.2}
                  step={0.01}
                  value={boswellVolAdj}
                  onChange={(e) => setBoswellVolAdj(Number(e.target.value))}
                />
              </label>
              <label className="assumption-control">
                <span>Netback adj (${netbackAdj.toFixed(2)}/{isOilDeal ? 'bbl' : 'Mcf'})</span>
                <input
                  type="range"
                  min={-0.5}
                  max={0.25}
                  step={0.01}
                  value={netbackAdj}
                  onChange={(e) => setNetbackAdj(Number(e.target.value))}
                />
              </label>
              <label className="assumption-control">
                <span>
                  Discount ({(discountRate * 100).toFixed(0)}%) → {live?.pvLabel || 'PV'}
                </span>
                <input
                  type="range"
                  min={0.05}
                  max={0.3}
                  step={0.01}
                  value={discountRate}
                  onChange={(e) => setDiscountRate(Number(e.target.value))}
                />
              </label>
              <label className="assumption-control">
                <span>ARO / P&amp;A (${aroMM.toFixed(1)}MM)</span>
                <input
                  type="range"
                  min={0}
                  max={5}
                  step={0.1}
                  value={aroMM}
                  onChange={(e) => setAroMM(Number(e.target.value))}
                />
              </label>
            </div>
            <div className="buyer-oh-panel">
              <div className="chart-title" style={{ fontSize: 13 }}>
                Buyer overhead (categories · judgment)
              </div>
              <p className="engine-note" style={{ margin: '2px 0 8px' }}>
                Buyer-side G&amp;A overlays — desk judgment, not seller facts. Custom $/mo with In CF
                applies to live PV / NTM. The ~$53k/mo total is an internal illustration until
                sourced/mapped.
              </p>
              {buyerOhCategories.map((c) => (
                <div className="buyer-oh-row" key={c.id}>
                  <span className="buyer-oh-label">{c.label}</span>
                  <input
                    type="number"
                    min={0}
                    step={500}
                    value={Number.isFinite(c.monthly) ? c.monthly : 0}
                    aria-label={`${c.label} monthly overhead`}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === '') {
                        handleBuyerOhChange(c.id, { monthly: 0 });
                        return;
                      }
                      const n = Number(raw);
                      if (Number.isFinite(n)) handleBuyerOhChange(c.id, { monthly: Math.max(0, n) });
                    }}
                  />
                  <label className="assumption-check">
                    <input
                      type="checkbox"
                      checked={!!c.includeInCf}
                      onChange={(e) => handleBuyerOhChange(c.id, { includeInCf: e.target.checked })}
                    />
                    <span>In CF</span>
                  </label>
                </div>
              ))}
              {live?.buyerOverhead && (
                <p className="engine-note" style={{ margin: '8px 0 0' }}>
                  Included in CF {formatCurrency(live.buyerOverhead.monthlyIncluded)}/mo · PV impact{' '}
                  {live.buyerOhPvDeltaMM != null ? `$${live.buyerOhPvDeltaMM.toFixed(2)}MM` : '—'}
                </p>
              )}
            </div>
          </div>
        </section>
      )}

      <KPIGrid>
        <KPICard
          label={live ? `Model ${live.pvLabel} (live)` : 'Boswell PDP PV10 share'}
          value={
            live
              ? formatCurrency(live.pvMM * 1e6)
              : `${(boswell.pv10Share * 100).toFixed(0)}%`
          }
          sub={
            live
              ? `After ARO $${live.bidBridgePvMM.toFixed(2)}MM · model-basis share ${(live.boswellShare * 100).toFixed(0)}% · NTM $${live.ntmCfMM.toFixed(2)}MM`
              : `$${boswell.pv10MM}MM of package`
          }
          color="var(--amber)"
        />
        <KPICard
          label={isOilDeal ? 'Recent rate' : 'Post-acid weekly rate'}
          value={`${formatInt(boswell.postAcidWeekly)} ${rateUnit}`}
          sub={
            isOilDeal
              ? `Observed trough ~${formatInt(boswell.troughMcfpd)} ${rateUnit}`
              : `Trough Jan–Mar ~${formatInt(boswell.troughMcfpd)}`
          }
          color="var(--green)"
        />
        <KPICard
          label={isOilDeal ? 'Value price band' : 'Midstream stack @ 6k'}
          value={
            isOilDeal
              ? 'WTI ±$10'
              : midstream?.stackAt6k != null
                ? `$${Number(midstream.stackAt6k).toFixed(2)}`
                : '—'
          }
          sub={
            isOilDeal
              ? 'Stress is oil −$5/bbl and netback −$2.50/bbl'
              : 'Approx $/MMBtu all-in · confirm MDQ vs contract'
          }
          color="var(--navy)"
        />
        <KPICard
          label="Bear 5× back-check"
          value={bearX5 != null ? formatCurrency(bearX5 * 1e6) : '—'}
          sub={isOilDeal ? 'Largest well −25% & WTI −$5 · not the bid' : 'Largest well −25% & HH −$0.50 · not the bid'}
          color="var(--red)"
        />
      </KPIGrid>

      <section className="dashboard-section">
        <div className="charts-grid charts-grid-2">
          <div className="chart-card">
            <div className="chart-title">{wellLabel} annual average rate</div>
            <div className="chart-subtitle">
              {rateUnit} · {boswell.actualsSource || 'DealPackage actuals'} · DE ~5–8%/yr recently
            </div>
            <BarChart
              data={boswell.annual}
              xKey="year"
              yKey="mcfpd"
              yFormat={formatInt}
              color="#E8A317"
            />
          </div>
          <div className="chart-card">
            <div className="chart-title">
              {isOilDeal ? 'Recent months' : 'Recent months (acid step-change)'}
            </div>
            <div className="chart-subtitle">
              Seller qi ~{formatInt(boswell.ariesQi)} {rateUnit}
              {boswell.acidStepPct != null ? ` · last30 vs prior60 ${boswell.acidStepPct > 0 ? '+' : ''}${boswell.acidStepPct}%` : ''}
            </div>
            <LineChart
              data={boswell.recentMonths}
              xKey="month"
              series={[{ key: 'mcfpd', label: rateUnit, color: '#E8A317', area: true }]}
              xType="time"
              yFormat={formatInt}
            />
          </div>
        </div>
      </section>

      {dca && (
        <section className="dashboard-section">
          <div className="section-title">Boswell DCA challenger vs seller forecast</div>

          <div className="chart-card" style={{ marginBottom: 16 }}>
            <div className="chart-title">
              Production diagnostics (facts) ·{' '}
              <span className={`pill ${dca.fitReady ? 'severity-low' : 'severity-high'}`}>
                {dca.fitReady == null ? 'fit-ready n/a' : dca.fitReady ? 'fit-ready' : 'NOT fit-ready'}
              </span>
            </div>
            <div className="chart-subtitle" style={{ marginBottom: 10 }}>
              Uptime / FTP / water / WGR from Boswell daily actuals · daily↔monthly reconciliation.
              These are diagnostics — the only volume judgment is <code>boswellVolAdj</code>.
              {!dca.fitReady && dca.fitReadyReasons?.length
                ? ` Fit-ready gate: ${dca.fitReadyReasons.join('; ')}.`
                : ''}
            </div>
            <KPIGrid>
              <KPICard
                label="Flowing-day uptime"
                value={
                  diag?.uptime?.flowingFraction != null
                    ? `${(diag.uptime.flowingFraction * 100).toFixed(0)}%`
                    : '—'
                }
                sub={
                  diag?.hasDaily
                    ? `${formatInt(diag.uptime.flowingDays)}/${formatInt(diag.uptime.days)} d · flowing ${formatInt(diag.uptime.avgFlowingRate)} vs cal ${formatInt(diag.uptime.calendarAvgRate)} Mcf/d`
                    : 'No daily series in DealPackage — run extract_deal_package.py'
                }
                color="var(--green)"
              />
              <KPICard
                label="FTP (flowing tubing psi)"
                value={diag?.ftp?.hasFtp ? `${formatInt(diag.ftp.latestFtp)} psi` : '—'}
                sub={
                  diag?.ftp?.hasFtp
                    ? `avg ${formatInt(diag.ftp.avgFtp)} · range ${formatInt(diag.ftp.minFtp)}–${formatInt(diag.ftp.maxFtp)}`
                    : 'FTP not emitted'
                }
                color="var(--navy)"
              />
              <KPICard
                label="Water-gas ratio"
                value={diag?.wgr?.hasWater ? `${diag.wgr.wgrBblPerMmcf} Bbl/MMcf` : '—'}
                sub={diag?.wgr?.hasWater ? `${formatInt(diag.wgr.n)} days with water` : 'Water not emitted'}
                color="var(--blue)"
              />
              <KPICard
                label="Daily↔monthly recon"
                value={
                  diag?.recon?.maxDeltaPct != null ? `${diag.recon.maxDeltaPct}% max Δ` : '—'
                }
                sub={
                  diag?.recon?.covered
                    ? `${formatInt(diag.recon.covered)} mo checked · ${diag.recon.withinTolerance ? 'within tol' : 'BREAK'}`
                    : 'No overlapping daily coverage'
                }
                color={diag?.recon?.withinTolerance === false ? 'var(--red)' : 'var(--amber)'}
              />
            </KPIGrid>
            <div className="assumption-grid" style={{ marginTop: 12 }}>
              <div>
                <div className="chart-title" style={{ fontSize: 13 }}>
                  {isOilDeal ? 'Rate step (Fact vs Judgment)' : 'Acid step (Fact vs Judgment)'}
                </div>
                <p className="engine-note">
                  <span className="tag tag-fact">Fact</span>{' '}
                  {isOilDeal ? 'earlier baseline' : 'pre-acid baseline'}{' '}
                  {diag?.acid?.preAcidBaselineMcfpd != null
                    ? `${formatInt(diag.acid.preAcidBaselineMcfpd)} ${rateUnit}`
                    : '—'}{' '}
                  → {isOilDeal ? 'recent peak' : 'post-acid peak'} {formatInt(dca.postAcidMcfpd)} {rateUnit}
                  {diag?.acid?.upliftPct != null ? ` (uplift ${diag.acid.upliftPct}%)` : ''}.{' '}
                  <span className="tag tag-judgment">Judgment</span> whether that uplift is durable —
                  see cases below.
                </p>
              </div>
              <div>
                <div className="chart-title" style={{ fontSize: 13 }}>Fit physics</div>
                <p className="engine-note">
                  Decline fit source: <strong>{dca.fitSource}</strong> · De {dca.DeUsedPct}%/yr
                  {dca.holdoutMapePct != null ? ` · holdout MAPE ${dca.holdoutMapePct}%` : ' · no holdout window'}.
                  {dca.hyperbolic
                    ? ` Hyperbolic candidate b ${dca.hyperbolic.b} (≤ ${dca.bMax}), Di ${dca.hyperbolic.DiPct}%/yr, R² ${dca.hyperbolic.r2}.`
                    : ' Hyperbolic fit unavailable (need ≥ 4 points).'}
                </p>
                <label className="assumption-control assumption-check" style={{ marginTop: 6 }}>
                  <span>Prefer hyperbolic (b-capped) fit context</span>
                  <input
                    type="checkbox"
                    checked={useHyperbolic}
                    onChange={(e) => setUseHyperbolic(e.target.checked)}
                  />
                </label>
              </div>
            </div>
            {dca.durabilityWarning && (
              <div className="assignability-banner open" style={{ marginTop: 12 }}>
                <strong>Durability caveat:</strong> {dca.durabilityWarning}
              </div>
            )}
          </div>

          <div className="chart-card" style={{ marginBottom: 16 }}>
            <div className="chart-subtitle" style={{ marginBottom: 10 }}>
              {dca.verdict} Fitted De ({dca.fitSource}) ~{dca.DeUsedPct}%/yr
              {dca.fit2020_2024?.r2 != null ? ` · R² ${dca.fit2020_2024.r2}` : ''}. Click Apply to push
              volume adj onto the live {isOilDeal ? 'volume' : 'Boswell'} knob (and Decision desk after you navigate).
            </div>
            <div className="charts-grid charts-grid-2">
              <div>
                <div className="chart-title">Actuals → alternate forecasts ({rateUnit})</div>
                <LineChart
                  data={dca.overlay}
                  xKey="month"
                  series={[
                    { key: 'actual', label: 'Actual', color: '#0C1F2E', area: true },
                    { key: 'aries', label: 'Seller', color: '#E8A317', dashed: true },
                    { key: 'holdAcid', label: 'Hold acid', color: '#10B981' },
                    { key: 'fadeTrough', label: 'Fade→trough', color: '#F97316' },
                    { key: 'troughStart', label: 'Trough', color: '#DC2626' },
                  ]}
                  xType="time"
                  yFormat={formatInt}
                />
              </div>
              <div>
                <div className="chart-title">Package PV impact at current knobs</div>
                <div className="chart-subtitle">
                  ΔPV vs seller volume (boswellVolAdj = 0) · {isOilDeal ? 'WTI' : 'HH'} ${hh.toFixed(2)} held
                </div>
                <DataTable
                  columns={[
                    { key: 'scenario', label: 'Case' },
                    { key: 'qi', label: 'qi', format: (v) => formatInt(v) },
                    {
                      key: 'volAdj',
                      label: 'Vol adj',
                      format: (v) => `${v > 0 ? '+' : ''}${(v * 100).toFixed(0)}%`,
                    },
                    { key: 'cum12', label: isOilDeal ? '12mo bbl' : '12mo Bcf', format: (v) => v.toFixed(1) },
                    { key: 'pv', label: 'PV $MM', format: (v) => `$${v.toFixed(2)}` },
                    {
                      key: 'dPv',
                      label: 'Δ vs seller',
                      format: (v) => `${v > 0 ? '+' : ''}$${v.toFixed(2)}M`,
                    },
                  ]}
                  data={dcaPvRows}
                  defaultSort="pv"
                />
                <div className="freeze-actions" style={{ marginTop: 12 }}>
                  {dca.scenarios.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      className={dcaCase === s.id ? 'btn-primary' : 'btn-secondary'}
                      onClick={() => applyDcaToKnob(s.id)}
                    >
                      Apply {s.label}
                    </button>
                  ))}
                </div>
                <p className="engine-note" style={{ marginTop: 10 }}>
                  Active DCA case: <strong>{dca.scenarios.find((s) => s.id === dcaCase)?.label}</strong> ·
                  {wellLabel} vol knob {(boswellVolAdj * 100).toFixed(0)}% · trough{' '}
                  {formatInt(dca.troughMcfpd)} {rateUnit} · {isOilDeal ? 'recent' : 'post-acid'}{' '}
                  {formatInt(dca.postAcidMcfpd)} {rateUnit} · seller qi {formatInt(dca.ariesQi)} {rateUnit}
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="dashboard-section">
        <div className="section-title">Scenario outputs ($MM) — engine · 5× is back-check only</div>
        <div className="chart-card" style={{ marginBottom: 16 }}>
          <BarChart
            data={scenarioRows}
            xKey="scenario"
            yKey="pv"
            horizontal
            yFormat={(v) => (v == null ? '—' : `$${Number(v).toFixed(1)}M`)}
            color={(d) =>
              d.id === 'bear' ? '#DC2626' : d.id === 'bull' ? '#10B981' : d.id === 'base' ? '#E8A317' : '#163A4F'
            }
            barLabel
          />
        </div>
        <DataTable
          columns={[
            { key: 'scenario', label: 'Scenario' },
            { key: 'pv', label: live?.pvLabel || 'Model PV', format: fmtMM },
            { key: 'ntm', label: 'NTM CF', format: fmtMM },
            { key: 'x4', label: '4× bc', format: fmtMM },
            { key: 'x5', label: '5× bc', format: fmtMM },
            { key: 'x6', label: '6× bc', format: fmtMM },
            { key: 'pv08', label: '0.8× PV', format: fmtMM },
          ]}
          data={scenarioRows}
          defaultSort="pv"
        />
      </section>

      <section className="dashboard-section">
        <div className="section-title">Netback · midstream assignability</div>
        {midstreamAssignability && (
          <div className={`assignability-banner ${midstreamAssignability.overallStatus}`}>
            <strong>Status: {midstreamAssignability.overallStatus.toUpperCase()}</strong>
            {' · '}
            {midstreamAssignability.overallNote}
          </div>
        )}
        {midstreamAssignability?.netbackBridge && (
          <div className="bid-bridge" style={{ margin: '12px 0 16px' }}>
            {midstreamAssignability.netbackBridge.map((row, i) => (
              <Fragment key={row.step}>
                {i > 0 && <div className="bid-bridge-arrow">→</div>}
                <div className="bid-bridge-step">
                  <span className={`tag tag-${row.class.toLowerCase()}`}>{row.class}</span>
                  <div className="bid-bridge-label">{row.step}</div>
                  <div className="bid-bridge-value">
                    {row.usdPerMmbtu < 0 ? '−' : ''}${Math.abs(row.usdPerMmbtu).toFixed(2)}
                  </div>
                </div>
              </Fragment>
            ))}
          </div>
        )}
        {midstream?.stack?.length || midstream?.demandByVol?.length ? (
          <div className="charts-grid charts-grid-2">
            {midstream?.stack?.length ? (
              <div className="chart-card">
                <div className="chart-title">Midstream unit cost stack</div>
                <div className="chart-subtitle">$/MMBtu at 6,000 MMBtu/d · firm demand rises if vols fall</div>
                <BarChart
                  data={midstream.stack}
                  xKey="component"
                  yKey="unit"
                  horizontal
                  yFormat={(v) => `$${v.toFixed(2)}`}
                  color="#163A4F"
                  barLabel
                />
              </div>
            ) : null}
            {midstream?.demandByVol?.length ? (
              <div className="chart-card">
                <div className="chart-title">Firm demand unit cost vs volume</div>
                <div className="chart-subtitle">
                  UI demand curve assumes ~10k MDQ for unitization — contract MDQ may be 30,000
                </div>
                <BarChart
                  data={midstream.demandByVol}
                  xKey="vol"
                  yKey="unit"
                  yFormat={(v) => `$${v.toFixed(2)}`}
                  color="#E8A317"
                  barLabel
                />
              </div>
            ) : null}
          </div>
        ) : null}
        {midstreamAssignability?.contracts?.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div className="chart-title">Contract assignability register</div>
            <div className="chart-subtitle" style={{ marginBottom: 10 }}>
              P0 open items · as-of {midstreamAssignability.asOf} · evidence paths stay in VDR
            </div>
            <DataTable
              columns={[
                { key: 'id', label: 'ID' },
                { key: 'counterparty', label: 'Counterparty' },
                { key: 'instrument', label: 'Instrument' },
                { key: 'status', label: 'Status' },
                { key: 'assignability', label: 'Assignability' },
                { key: 'bidImpact', label: 'Bid impact' },
                { key: 'owner', label: 'Owner' },
              ]}
              data={midstreamAssignability.contracts}
              defaultSort="id"
              defaultDir="asc"
            />
          </div>
        )}
      </section>
    </div>
  );
}
